<template>
  <div
    ref="refEl"
    class="faq-article"
  >
    <h1 class="faq-title">{{ props.rubric?.title }}</h1>
    <div class="faq-menu">
      <button
        v-for="item in questions"
        :key="item.question"
        class="faq-menu__item"
        @click="scrollTo(item.id.toString())"
      >
        {{ item.question }}
      </button>
    </div>
    <div class="faq-questions">
      <div
        v-for="question in questions"
        :key="question.id"
      >
        <h2
          :id="question.id.toString()"
          class="faq-questions__title"
        >
          {{ question.question }}
          <Controls :question-id="question.id" />
        </h2>
        <div
          class="faq-questions__answer"
          v-html="question.answer_rendered || question.answer"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { HelpCenterRubric } from '@/shared/api/helpCenter';
  import Controls from './Controls.vue';

  interface IProps {
    rubric: HelpCenterRubric;
  }

  const props = defineProps<IProps>();
  const questions = computed(() => props.rubric?.questions);
  const route = useRoute();

  const refEl = ref<HTMLElement | null>(null);
  const root = computed(() => refEl.value?.getRootNode?.() as ShadowRoot | Document);

  const scrollTo = (id: string) => {
    const el = root.value.getElementById(id);
    const yOffset = -80;

    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  watch(
    questions,
    (newVal) => {
      if (newVal?.length && route.hash) {
        nextTick(() => {
          scrollTo(route.hash.substring(1));
        });
      }
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .faq-title {
    @include typography.base-1000-950-extra;

    @include adaptivity.tablet {
      @include typography.base-800-750-extra;
    }
  }

  .faq-menu {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 16px;
  }

  .faq-menu__item {
    text-decoration: underline solid 1px;
    cursor: pointer;
    user-select: none;
    text-decoration-color: var(--alias-color-text-subtler);
    text-underline-offset: 4px;
    text-decoration-line: underline;
    text-decoration-thickness: 1px;
    background: none;
    border: none;
    text-align: left;
    font-family: inherit;
    color: inherit;

    @include typography.base-400-400-regular;
  }

  .faq-menu__item:hover {
    color: var(--alias-color-link-darked-hovered);
    text-decoration-color: var(--alias-color-link-darked-hovered);
  }

  .faq-questions__title {
    display: flex;
    gap: 8px;
    justify-content: space-between;

    @include typography.base-800-750-extra;

    @include adaptivity.tablet {
      @include typography.base-700-500-extra;
    }
  }

  :deep(.toast-wrap) {
    top: 90px;

    @include adaptivity.tablet {
      top: inherit;
    }
  }

  :deep(.toast-text) {
    flex-direction: row;
    column-gap: 8px;

    @include typography.base-400-400-medium;
  }
</style>
